// The Meet Points page's ranking and words (docs/school-carnivals-points-slice-1.md in the portal repo).
//
// The page lists each athlete's points at one meet, worked out on the server by the same rules as the
// season's points. Athletes are placed by total points: equal totals share a place, and the next place
// follows on (1, 1, 2). Points slice 6 (Doug, 2026-10-26): the tiebreak by 1st places, then places that
// score, is gone ("We need to change the tiebreak rules"), as no school breaks a tie and the schools whose
// results show it go on to the next place; the server's Champions.Places says the same.

// "1st", "2nd", "3rd", "4th", ... "11th", "12th", "13th", ... "21st".
export const placeLabel = (place) => {
  const n = Number(place);
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

// "1 point", "4 points".
export const pointsLabel = (points) => `${points} ${Number(points) === 1 ? 'point' : 'points'}`;

// Points order: total points (higher first); athletes on the same total, who share a place, by last
// name, then first name.
export const comparePoints = (a, b) => {
  if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
  return (
    (a.lastName || '').localeCompare(b.lastName || '') ||
    (a.firstName || '').localeCompare(b.firstName || '')
  );
};

// A copy of each athlete with its place, in points order. Equal totals share a place, and the next
// place follows on (1, 1, 2).
export const rankByPoints = (athletes) => {
  let rank = 1;
  let previous = null;
  return [...(athletes || [])].sort(comparePoints).map((athlete) => {
    if (previous !== null && athlete.totalPoints < previous.totalPoints) rank += 1;
    previous = athlete;
    return { ...athlete, rank };
  });
};

// When the Meet Points page was worked out, as the page shows it: "Oct 24, 2026, 5:41 PM", or N/A. The
// server sends UTC without a zone, so one is added before reading it. Shown on the athletes table (a club)
// or the House Totals card (a school).
export const formatUpdatedAt = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+')) {
      utcDateString = dateString + 'Z';
    }

    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return 'N/A';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

// ---- Who sees the Points page (points slice 4b) ----

// The Points page's dropdown "Who sees this page", as the server sends and saves it: Admins only (a meet
// never set reads so) or Admins and members.
export const TEAM_TOTALS_VISIBILITY = { ADMINS: 'admins', MEMBERS: 'members' };

// What the Points page offers the signed-in user. An Admin sees the page live (it checks for new results
// every minute), exports it, and for a school chooses who else sees it. A school's members see it once an
// Admin has released it, read only, and it does not check for new results by itself (Doug, 2026-10-25:
// "Too many users can leave the page up and hammer the server").
export const pointsPageControls = (isAdmin, isSchool) => ({
  whoSeesThisPage: isAdmin && isSchool,
  exportFile: isAdmin,
  liveUpdates: isAdmin,
});

// ---- House Points (points slice 5) ----

// Whether the House Points report has anything to show: a house with points, or a deduction, at any of the
// season's counted meets. Before that (no houses, no counted meet, or every total still 0) the page says "No
// house points this season yet." and Export to file is off.
export const seasonHasHousePoints = (report) =>
  (report?.houses || []).some((house) => (house.meetPoints || []).some((points) => Number(points) !== 0));

// ---- The group view's lists (points slice 6) ----

// The Points page's group view, list by list: each athlete is in the list the server gives them (listKey:
// a combined group's key, "16-17", when the meet's program ticks Combine ages (or years) for every event
// their age ran; otherwise their own number; listName: that group's name), girls and boys apart. Youngest
// first (by the list's lowest number), girls before boys, a number's own list before a combined one that
// starts at it. A reply from before slice 6 (no listKey) lists by the athlete's own number.
const isOwnNumber = (key) => /^\d+$/.test(key);

export const groupByList = (athletes) => {
  const lists = new Map();
  (athletes || []).forEach((athlete) => {
    const key = athlete.listKey || String(athlete.age);
    const id = `${key}|${athlete.gender}`;
    if (!lists.has(id)) {
      lists.set(id, { key, name: athlete.listName || null, gender: athlete.gender, athletes: [] });
    }
    lists.get(id).athletes.push(athlete);
  });
  return [...lists.values()].sort(
    (a, b) =>
      parseInt(a.key, 10) - parseInt(b.key, 10) ||
      (a.gender || '').localeCompare(b.gender || '') ||
      Number(!isOwnNumber(a.key)) - Number(!isOwnNumber(b.key))
  );
};
